import Image from 'next/image';
import { cn } from '@/utils/cn';
import LivescorePlayerStats from '@/components/atoms/LivescorePlayerStats/LivescorePlayerStats';
import LivescoreEntityLink from '@/components/atoms/LivescoreEntityLink/LivescoreEntityLink';
import { ApiH2hGet200ResponseDataH2hHomeInner } from '@/api/livescore/generated';
import ArrowRightCorner from '@/components/icons/ArrowRightCorner';

export const formatDateToDDMM = (isoString: string | undefined | null): string => {
  if (!isoString) {
    return '';
  }

  const date = new Date(isoString);

  if (isNaN(date.getTime())) {
    return '';
  }

  const day = date.getDate();
  const month = date.getMonth() + 1;

  const formattedDay = String(day).padStart(2, '0');
  const formattedMonth = String(month).padStart(2, '0');

  return `${formattedDay}/${formattedMonth}`;
};

const PlayerAchievment = ({ label, value, gameplay }: { label: string; value?: string; gameplay?: boolean }) => (
  <div className="px-2 flex items-center py-[1.33px] dark:bg-black-500 bg-white border dark:border-border-400 border-gray-200 rounded-md relative">
    <span className={cn('font-semibold text-[10.64px]', gameplay && 'font-gameplay')}>{label}</span>
    {value ? (
      <span className="z-20 absolute -top-1 -right-1 dark:bg-white bg-black rounded-full dark:text-black-500 text-white w-2.5 h-2.5 flex items-center justify-center font-bold text-[7px]">
        {value}
      </span>
    ) : null}
  </div>
);

const LivescoreMatchCard = ({
  match,
  playerPosition,
  player,
  mainCompetitorId,
  showWinnerStatus = true,
  isAhref = false,
}: {
  match: ApiH2hGet200ResponseDataH2hHomeInner;
  playerPosition?: string;
  player?: boolean;
  mainCompetitorId?: string;
  showWinnerStatus?: boolean;
  isAhref?: boolean;
}) => {
  const mainCompetitorGoals =
    match.competitors.find(({ main, id }) => main === true || id === mainCompetitorId)?.goals ?? undefined;
  const diffCompetitorGoals =
    match.competitors.find(
      ({ main, id }) =>
        (main === false && mainCompetitorId === undefined) || (id !== mainCompetitorId && main === undefined),
    )?.goals ?? undefined;
  const doesWon =
    mainCompetitorGoals !== undefined && diffCompetitorGoals !== undefined
      ? mainCompetitorGoals > diffCompetitorGoals
      : undefined;

  const doesDraw =
    mainCompetitorGoals !== undefined && diffCompetitorGoals !== undefined
      ? mainCompetitorGoals === diffCompetitorGoals
      : undefined;

  return (
    <LivescoreEntityLink
      href={`/livescore/match/${match.id.split(':')[2]}`}
      className="flex w-full items-center justify-between px-4 py-2 sm:gap-4 gap-3 dark:bg-black-400 bg-white_theme-50 rounded-lg text-black dark:text-white"
      isAhref={isAhref}
    >
      <p className="font-bold sm:text-[9px] text-[7px] text-gray-400">{formatDateToDDMM(match.date)}</p>
      <div className="w-full flex items-center justify-between">
        <div className="sm:space-y-0 space-y-1 w-full">
          {match.competitors.map(({ name, logo_url, abbreviation, main, id, aggregate_winner, red_cards }) => (
            <div className="flex items-center gap-2 w-full">
              <div className="relative sm:h-4 sm:w-4 w-3 h-3">
                {logo_url ? (
                  <Image src={logo_url} alt={name} layout="fill" objectFit="contain" />
                ) : (
                  <div className="sm:h-4 sm:w-4 w-3 h-3 rounded-full bg-primary-600 sm:text-[6px] text-[4px] flex items-center justify-center">
                    {abbreviation}
                  </div>
                )}
              </div>
              <p
                className={cn(
                  'sm:text-[13px] text-[10px] w-fit line-clamp-1 overflow-ellipsis',
                  doesWon
                    ? (main || id === mainCompetitorId) && 'font-bold'
                    : ((!main && mainCompetitorId === undefined) || id !== mainCompetitorId) && 'font-semibold',
                )}
              >
                {name}
              </p>
              { red_cards ? (
                <span className="text-white font-bold text-[9px] h-[14px] w-[10px] rounded-[3px] bg-[#FF0004] flex items-center justify-center">
                  {red_cards}
                </span>
              ) : null}
              { aggregate_winner ? (
                <span className='w-min inline-block scale-50 sm:scale-[85%]'>
                  <ArrowRightCorner color="#31C46C" width={16} />
                </span>
              ) : null}
            </div>
          ))}
        </div>
        {player && match.player_statistics && (
          <div className="flex items-center w-full justify-end pr-6">
            {match.player_statistics.minutes_played ? (
              <PlayerAchievment label={`${match.player_statistics.minutes_played}’`} />
            ) : null}
            {match.player_statistics.assists ? (
              <PlayerAchievment label="A" gameplay value={match.player_statistics.assists.toString()} />
            ) : null}
            {match.player_statistics.goals_scored ? (
              <PlayerAchievment label="G" gameplay value={match.player_statistics.goals_scored.toString()} />
            ) : null}
            {playerPosition ? (
              <LivescorePlayerStats stats={match.player_statistics} position={playerPosition} className="ml-2" />
            ) : null}
          </div>
        )}
        {player && !match.player_statistics && (
          <div className="w-full flex items-center justify-end pr-6">
            <span className="dark:bg-black-400 bg-white border dark:border-border-400 border-gray-200 text-[10.64px] px-[5.32px] py-[2.66px] rounded-md">
              poza składem
            </span>
          </div>
        )}
        <div className="flex flex-col items-center sm:gap-0 gap-1">
          {match.competitors.map(({ goals }) => (
            <p className="sm:text-[11px] text-[8px] font-gameplay">{goals}</p>
          ))}
        </div>
      </div>
      {showWinnerStatus && doesWon !== undefined ? (
        <div
          className={cn(
            '!text-white sm:w-5 sm:h-5 sm:min-w-5 min-w-[15px] w-[15px] h-[15px] rounded-sm flex items-center justify-center font-bold sm:text-[10.64px] text-[8px]',
            doesWon ? 'bg-[#06C000]' : 'bg-[#FF0004]',
            doesDraw && 'bg-[#FFA600]',
          )}
        >
          {doesWon ? 'Z' : doesDraw ? 'R' : 'P'}
        </div>
      ) : null}
    </LivescoreEntityLink>
  );
};

export default LivescoreMatchCard;
